import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, Loader2, Eye, HandHeart, Compass, Printer } from 'lucide-react';
import { RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, ResponsiveContainer } from 'recharts';
import Header from '../components/Header';
import Footer from '../components/Footer';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const api = axios.create({ baseURL: `${API_URL}/api` });

const SECTION_ICONS = { awareness: Eye, acceptance: HandHeart, action: Compass };
const SECTION_ACCENT = {
  awareness: '#2C5530',
  acceptance: '#BC562B',
  action: '#1A2E1D',
};

const BandChip = ({ band }) => (
  <span className="inline-block text-[10px] font-semibold uppercase tracking-[0.25em] bg-[#F2F0E9] text-[#57534E] px-2.5 py-1">
    {band}
  </span>
);

const ScoreDial = ({ score, color, label }) => (
  <div className="flex flex-col items-center">
    <div className="relative w-32 h-32 md:w-40 md:h-40">
      <svg className="w-full h-full -rotate-90" viewBox="0 0 100 100">
        <circle cx="50" cy="50" r="45" fill="none" stroke="#F2F0E9" strokeWidth="8" />
        <circle
          cx="50" cy="50" r="45" fill="none" stroke={color} strokeWidth="8" strokeLinecap="round"
          strokeDasharray={`${(score / 100) * 283} 283`}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="font-['Playfair_Display'] text-3xl md:text-4xl text-[#1A1A1A]">{score}</span>
        <span className="text-[10px] uppercase tracking-wider text-[#A8A29E]">/ 100</span>
      </div>
    </div>
    <p className="text-xs uppercase tracking-[0.25em] mt-3 font-semibold" style={{ color }}>{label}</p>
  </div>
);

const SectionCard = ({ section, index }) => {
  const Icon = SECTION_ICONS[section.key];
  const color = SECTION_ACCENT[section.key];
  const dims = Object.entries(section.dimensions || {});
  return (
    <div className="bg-white border-t-4 border-[#E7E5E4] p-6 md:p-8" style={{ borderTopColor: color }} data-testid={`result-section-${section.key}`}>
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="flex items-start gap-3">
          <Icon className="w-7 h-7 mt-1" style={{ color }} />
          <div>
            <p className="text-[10px] uppercase tracking-[0.3em] text-[#A8A29E]">0{index + 1} — {section.label}</p>
            <h3 className="font-['Playfair_Display'] text-2xl md:text-3xl text-[#1A1A1A]">{section.label}</h3>
            <p className="text-xs uppercase tracking-wider text-[#57534E] mt-1">{section.principle}</p>
          </div>
        </div>
        <div className="text-right">
          <div className="font-['Playfair_Display'] text-4xl md:text-5xl" style={{ color }}>{section.score}</div>
          <BandChip band={section.band} />
        </div>
      </div>

      <p className="text-[#1A1A1A] text-sm md:text-base leading-relaxed mb-4">{section.headline}</p>

      <div className="bg-[#FAFAF9] border-l-4 p-4 mb-4" style={{ borderLeftColor: color }}>
        <p className="text-[10px] uppercase tracking-[0.25em] font-semibold text-[#A8A29E] mb-1">Your opportunity</p>
        <p className="text-sm text-[#57534E] leading-relaxed">{section.opportunity}</p>
      </div>

      <div className="text-sm text-[#1A1A1A] mb-6">
        <span className="text-[10px] uppercase tracking-[0.25em] font-semibold text-[#A8A29E] block mb-1">Consider</span>
        <p className="italic leading-relaxed">"{section.consider}"</p>
      </div>

      {dims.length > 0 && (
        <div>
          <p className="text-[10px] uppercase tracking-[0.25em] font-semibold text-[#A8A29E] mb-3">Sub-dimensions</p>
          <ul className="space-y-2">
            {dims.map(([key, d]) => (
              <li key={key} data-testid={`result-dim-${section.key}-${key}`}>
                <div className="flex justify-between items-center mb-1 text-sm">
                  <span className="text-[#1A1A1A]">{d.label}</span>
                  <span className="font-semibold text-[#57534E]">{d.score}</span>
                </div>
                <div className="h-1.5 bg-[#F2F0E9]">
                  <div className="h-full transition-all" style={{ width: `${d.score}%`, backgroundColor: color }} />
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

const A3ResultPage = () => {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/assessment/a3/result/${id}`).then(({ data }) => setData(data)).catch(() => setLoading(false)).finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAF9]">
        <Header />
        <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#BC562B]" /></div>
        <Footer />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-[#FAFAF9]">
        <Header />
        <div className="min-h-[60vh] flex items-center justify-center flex-col gap-4">
          <p className="text-[#57534E]">We couldn't find that result.</p>
          <Link to="/a3-assessment" className="text-[#BC562B] underline">Take the assessment</Link>
        </div>
        <Footer />
      </div>
    );
  }

  const { result, demographics } = data;
  const radarData = result.sections.map((s) => ({ area: s.label, score: s.score, full: 100 }));

  return (
    <div className="min-h-screen bg-[#FAFAF9] print:bg-white" data-testid="a3-result-page">
      <div className="print:hidden"><Header /></div>

      <section className="py-16 md:py-20">
        <div className="container mx-auto px-6 md:px-12 max-w-5xl">
          <div className="flex items-start justify-between gap-4 mb-10 flex-wrap">
            <div>
              <p className="text-[#BC562B] text-xs uppercase tracking-[0.35em] mb-3">A³ Performance Profile</p>
              <h1 className="font-['Playfair_Display'] text-4xl md:text-5xl text-[#1A1A1A] leading-[1.1] mb-3">
                {demographics.name}'s A³ Profile
              </h1>
              <p className="text-[#57534E]">Think better. Move better. Play freer.</p>
            </div>
            <button onClick={() => window.print()} className="print:hidden inline-flex items-center gap-2 border border-[#57534E] text-[#57534E] px-4 py-2 text-xs uppercase tracking-wider font-semibold hover:bg-[#F2F0E9]" data-testid="a3-print-btn">
              <Printer className="w-4 h-4" /> Print / PDF
            </button>
          </div>

          {/* Top scoreboard */}
          <div className="bg-white border border-[#E7E5E4] p-6 md:p-10 mb-10">
            <div className="grid md:grid-cols-4 gap-6 items-center">
              {result.sections.map((s) => (
                <ScoreDial key={s.key} score={s.score} color={SECTION_ACCENT[s.key]} label={s.label} />
              ))}
              <div className="flex flex-col items-center border-t md:border-t-0 md:border-l border-[#E7E5E4] pt-6 md:pt-0 md:pl-6" data-testid="result-overall">
                <p className="text-[10px] uppercase tracking-[0.3em] text-[#A8A29E] mb-2">A³ Score</p>
                <div className="font-['Playfair_Display'] text-6xl md:text-7xl text-[#1A1A1A]">{result.overall.score}</div>
                <span className="text-xs text-[#57534E]">/ 100</span>
                <div className="mt-3"><BandChip band={result.overall.band} /></div>
              </div>
            </div>

            {/* Shape */}
            <div className="mt-10 pt-8 border-t border-[#F2F0E9] grid md:grid-cols-[1fr_1fr] gap-8 items-center">
              <div className="h-64" data-testid="result-radar">
                <ResponsiveContainer width="100%" height="100%">
                  <RadarChart data={radarData}>
                    <PolarGrid stroke="#E7E5E4" />
                    <PolarAngleAxis dataKey="area" tick={{ fill: '#57534E', fontSize: 12, fontWeight: 600 }} />
                    <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fill: '#A8A29E', fontSize: 10 }} tickCount={6} />
                    <Radar dataKey="score" stroke="#BC562B" fill="#BC562B" fillOpacity={0.55} strokeWidth={2.5} dot={{ fill: '#BC562B', r: 4 }} />
                  </RadarChart>
                </ResponsiveContainer>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-[0.3em] text-[#A8A29E] mb-2">Your profile</p>
                <h2 className="font-['Playfair_Display'] text-2xl md:text-3xl text-[#1A1A1A] mb-3" data-testid="result-profile-type">{result.profile_type_label}</h2>
                <p className="text-[#57534E] text-sm md:text-base leading-relaxed mb-4">{result.profile_type_summary}</p>
                <p className="text-sm text-[#57534E]">
                  <span className="text-[#1A1A1A] font-semibold">Your strongest area:</span> {result.strongest.section_label}
                </p>
                <p className="text-sm text-[#57534E] mt-1">
                  <span className="text-[#1A1A1A] font-semibold">Your greatest development opportunity:</span> {result.focus.section_label}
                </p>
              </div>
            </div>
          </div>

          {/* Section deep-dives */}
          <div className="space-y-6">
            {result.sections.map((s, i) => (<SectionCard key={s.key} section={s} index={i} />))}
          </div>

          {/* Development focus + CTA */}
          <div className="bg-[#1A2E1D] text-white p-8 md:p-12 mt-10" data-testid="result-focus">
            <p className="text-[#BC562B] text-[10px] uppercase tracking-[0.35em] mb-3">Your A³ development focus</p>
            <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl !text-white mb-4">{result.focus.section_label}</h2>
            <p className="text-white/85 mb-6 max-w-2xl">
              Your profile suggests that your next opportunity may be to develop your ability to respond to
              what is happening without first needing it to be different.
            </p>

            <div className="bg-white/5 border-l-2 border-[#BC562B] p-5 mb-6">
              <p className="text-[10px] uppercase tracking-[0.25em] text-[#BC562B] font-semibold mb-2">A³ practice</p>
              <p className="text-white/90 text-sm leading-relaxed">
                The next time you hit a poor shot: <span className="text-white font-semibold">notice it, allow it, choose what comes next.</span>
                Don't try to change the feeling. Don't replay the shot. Don't predict the result. Simply return to the process.
              </p>
            </div>

            <p className="text-[10px] uppercase tracking-[0.3em] text-white/60 mb-2">Recommended next step</p>
            <h3 className="font-['Playfair_Display'] text-2xl md:text-3xl !text-white mb-3">A³ Performance Programme</h3>
            <p className="text-white/85 text-sm max-w-2xl mb-6">
              A structured six-week programme designed to take your A³ profile from understanding into practice.
              Awareness → Acceptance → Action.
            </p>

            <div className="flex flex-col sm:flex-row gap-3">
              <Link to="/contact" className="inline-flex items-center gap-2 bg-[#BC562B] text-white px-6 py-3 text-xs uppercase tracking-[0.2em] font-semibold hover:bg-[#9A4520]" data-testid="result-cta-primary">
                Enquire about coaching <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/the-approach" className="inline-flex items-center gap-2 border border-white/40 text-white px-6 py-3 text-xs uppercase tracking-[0.2em] font-semibold hover:bg-white/10" data-testid="result-cta-secondary">
                Explore the QMG Blueprint
              </Link>
            </div>
          </div>

          <p className="text-xs text-[#A8A29E] mt-10 max-w-2xl">
            The aim isn't to control every thought. It's to change your relationship with what happens.
            <br />
            Bands describe the self-reported pattern shown by the assessment — not a clinical diagnosis, ability rating, or guarantee of golf performance.
          </p>
        </div>
      </section>

      <div className="print:hidden"><Footer /></div>
      <style>{`
        @media print {
          [data-testid="a3-result-page"] { background: white !important; }
          .print\\:hidden { display: none !important; }
        }
      `}</style>
    </div>
  );
};

export default A3ResultPage;
